import { VersionRange } from '../../models/core/version-range.js';
import { DataModelBuilder, isSameData, SerializationContext, SerializedData } from '../../models/dto/dto.js';
import { MapLocation } from '../../models/serializable/map-location.js';
import { MapUtils } from '../../utility/map-utils.js';
import { Component } from '../core/component.js';

/**
 * @deprecated Business place/location documents are legacy. Use specific location models for new features.
 */
export class BusinessLocationComponent extends Component {
  static readonly TYPE_ID: string = 'a9b3401166e54d87981a5841d8b4c68f';
  static readonly SCHEMA_VERSION: number = 3;
  static readonly DEFAULT_RENDER_ORDER: number = 11;

  constructor(
    public readonly location: MapLocation,
  ) {
    super();
  }

  get $typeId(): string {
    return BusinessLocationComponent.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(BusinessLocationComponent.SCHEMA_VERSION);
  }

  get $removable(): boolean {
    return false;
  }

  static empty(): BusinessLocationComponent {
    return new BusinessLocationComponent(
        MapLocation.empty(),
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): BusinessLocationComponent {
    return new BusinessLocationComponent(
        context.fromData<MapLocation>(MapUtils.getRaw(map, 'location') as SerializedData),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'location': context.toData(this.location),
    };
  }

  clone(): this {
    return new BusinessLocationComponent(
        this.location.clone(),
    ) as this;
  }

  isSame(other: BusinessLocationComponent): boolean {
    if(this === other) {
      return true;
    }

    return isSameData(other.location, this.location);
  }
}

export class BusinessLocationComponentBuilder extends DataModelBuilder<BusinessLocationComponent> {
  build(context: SerializationContext, map: SerializedData): BusinessLocationComponent {
    return BusinessLocationComponent.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(BusinessLocationComponent.SCHEMA_VERSION)) {
      return map;
    }

    if(versionRange.isGreaterThan(BusinessLocationComponent.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version [BusinessLocationComponent] was converted from type
    // [Component] to type [RenderableComponent].
    if(version < 2) {
      MapUtils.setIfNullOrUndefined(map, 'renderOrder', BusinessLocationComponent.DEFAULT_RENDER_ORDER);
      MapUtils.setIfNullOrUndefined(map, 'isRenderingEnabled', true);
      version = 2;
    }

    // In this version [BusinessLocationComponent] was converted from type
    // [RenderableComponent] to type [Component].
    if(version < 3) {
      version = 3;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
